import React, { memo, useLayoutEffect, useState } from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Flex from '@atlaskit/primitives/components/flex';
import { xcss } from '@atlaskit/primitives/xcss';
import Spinner from '@atlaskit/spinner/spinner';

// eslint-disable-next-line import/no-cycle
import { BATCH_SIZE, type SharedCommentBatchProps } from './CommentBatchList.tsx';

const spinnerContainerStyles = xcss({
	margin: 'auto',
	position: 'sticky',
	bottom: '1px',
});

// Exported for testing
export const SPINNER_DURATION = 100;

type CommentBatchProps<TComment> = SharedCommentBatchProps<TComment> & {
	commentsList: TComment[];
	canRenderBatch: boolean;
	batchIndex: number;
	onBatchRendered: (batchIndex: number) => void;
	transitionIndexWithinBatch: number | undefined;
};

const CommentBatchInner = <TComment,>({
	passThroughProps,
	Component,
	getElementsToBatch,
	commentsList,
	canRenderBatch,
	batchIndex,
	onBatchRendered,
	transitionIndexWithinBatch,
}: CommentBatchProps<TComment>): React.JSX.Element | null => {
	const [showSpinner, setShowSpinner] = useState(false);

	useLayoutEffect(() => {
		if (canRenderBatch) {
			setShowSpinner(true);
			setTimeout(() => {
				onBatchRendered(batchIndex);
				setShowSpinner(false);
			}, SPINNER_DURATION);
		}
	}, [canRenderBatch, batchIndex, onBatchRendered]);

	if (!canRenderBatch) {
		return null;
	}

	const batchOffset = batchIndex * BATCH_SIZE;
	const batchedList = commentsList.slice(batchOffset, batchOffset + BATCH_SIZE);

	const elements = getElementsToBatch(
		batchedList,
		passThroughProps,
		Component,
		transitionIndexWithinBatch,
	);

	return (
		<>
			{elements}
			{showSpinner ? (
				<Flex justifyContent="center" alignItems="center" xcss={spinnerContainerStyles}>
					<Spinner interactionName="comment-batch" size="medium" />
				</Flex>
			) : null}
		</>
	);
};

// `memo` does not preserve generics, so we cast the memoized component back to a generic
// function type to keep `TComment` inferrable at the call site.
export const CommentBatch = memo(CommentBatchInner) as typeof CommentBatchInner;
